export type ConfirmedBid = {
  id: string;
  lastBidAmount: number;
  lastBidDate: string;
  lastBidOrg?: string;
  orgTax: number;
};

// A `/hall-posts` card as enterprise-ads-api sends it. A post nobody has bid on
// in the cycle has no Redis bid state yet, so its three bid fields arrive null.
export type HallCardWire<T extends ConfirmedBid> = Omit<
  T,
  "lastBidAmount" | "orgTax" | "lastBidOrg"
> & {
  lastBidAmount: number | null;
  orgTax: number | null;
  lastBidOrg?: string | null;
};

// The server reads an empty bid state as 0 and adds the first increment to it,
// so "no bid yet" is a confirmed 0. Only a missing value is filled: any other
// non-finite amount still reaches bidCandidate and is refused there. The leader
// becomes "", never undefined, so it cannot equal a session org id that has
// not loaded yet and block the first bid as if this org already led.
export function withUnbidDefaults<T extends ConfirmedBid>(card: HallCardWire<T>): T {
  return {
    ...card,
    lastBidAmount: card.lastBidAmount ?? 0,
    orgTax: card.orgTax ?? 0,
    lastBidOrg: card.lastBidOrg ?? "",
  } as T;
}

export function bidCandidate(confirmed: number, increment: number): number | undefined {
  const amount = confirmed + increment;
  if (
    !Number.isFinite(confirmed) ||
    confirmed < 0 ||
    !Number.isFinite(increment) ||
    increment <= 0 ||
    !Number.isSafeInteger(Math.round(amount * 100))
  )
    return undefined;
  return amount;
}

// A preview never becomes an input to another preview or to a server update.
export function displayedCardBid(confirmed: number, increment: number, preview: boolean): number {
  return preview ? (bidCandidate(confirmed, increment) ?? confirmed) : confirmed;
}

export function isCurrentBid(
  previous: ConfirmedBid | undefined,
  incoming: ConfirmedBid,
  windowStart?: number,
): boolean {
  if (!Number.isFinite(incoming.lastBidAmount) || incoming.lastBidAmount < 0) return false;
  const after = Date.parse(incoming.lastBidDate);
  if (windowStart !== undefined && (!Number.isFinite(after) || after < windowStart)) return false;
  if (!previous) return true;
  if (incoming.lastBidAmount < previous.lastBidAmount) return false;
  const before = Date.parse(previous.lastBidDate);
  return !Number.isFinite(before) || (Number.isFinite(after) && after >= before);
}

export function remainingBidCooldown(lastBidDate: string, now = Date.now()): number {
  const timestamp = Date.parse(lastBidDate);
  if (!Number.isFinite(timestamp)) return 0;
  return Math.max(0, Math.min(10, Math.ceil((timestamp + 10_000 - now) / 1000)));
}
